import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, LinearProgress } from '@mui/material';
import api from '../api/api';
import { portalRequest } from './portalAccess';

const BRAND_SUCCESS = '#a5652c';
const BRAND_TEXT_SECONDARY = '#6f7074';

/**
 * Динамический код получения (TOTP, 5 минут).
 * Сам тикает таймер, при истечении — запрашивает новый код с бэка.
 */
export default function PickupCodeBlock({ token, session }) {
  const [code, setCode] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [windowSeconds, setWindowSeconds] = useState(300);
  const [error, setError] = useState(null);
  const [used, setUsed] = useState(false);
  useEffect(() => {
    // Each order owns its timer and requests. A late response from the previous
    // order must never replace the code for the currently visible order.
    let active = true;
    let stopped = false;
    let fetching = false;
    let remaining = 0;
    const controller = new AbortController();
    setUsed(false);
    setError(null);
    setCode(null);
    setSecondsLeft(0);
    if (!session) { setError('Войдите в кабинет для получения кода'); return; }
    const fetchCode = async () => {
      if (!active || stopped || fetching) return;
      fetching = true;
      try {
        const res = await api.get(`/client-portal/order/${token}/pickup-code`, portalRequest(session, { signal: controller.signal }));
        if (!active) return;
        remaining = res.data.seconds_left;
        setCode(res.data.code);
        setSecondsLeft(remaining);
        setWindowSeconds(res.data.window_seconds);
      } catch (e) {
        if (!active) return;
        stopped = true;
        const status = e?.response?.status;
        if (status === 410) setUsed(true);
        else if (status === 401) setError('Сессия завершена. Войдите в кабинет заново');
        else if (status === 403) setError('Код будет доступен после оплаты');
        else if (status === 404) setError('Заказ не найден');
        else setError('Не удалось получить код');
      } finally {
        fetching = false;
      }
    };
    fetchCode();
    const interval = setInterval(() => {
      if (!active || stopped) return;
      remaining = Math.max(0, remaining - 1);
      setSecondsLeft(remaining);
      if (remaining === 0) fetchCode();
    }, 1000);
    return () => {
      active = false;
      clearInterval(interval);
      controller.abort();
    };
  }, [token, session]);

  if (used) {
    return (
      <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px dashed #94A3B8', bgcolor: '#f7f5f1' }}>
        <Typography variant="caption" color={BRAND_TEXT_SECONDARY} fontWeight={600}>
          Код получения уже использован
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={0} sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px dashed #FCA5A5', bgcolor: '#FEF2F2' }}>
        <Typography variant="caption" color="#B91C1C">{error}</Typography>
      </Paper>
    );
  }

  const progress = windowSeconds > 0 ? (secondsLeft / windowSeconds) * 100 : 0;
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        border: `1px dashed ${BRAND_SUCCESS}`,
        bgcolor: '#fbf2e9',
      }}
    >
      <Typography
        variant="caption"
        color={BRAND_TEXT_SECONDARY}
        fontWeight={600}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        Код получения (динамический)
      </Typography>
      <Typography
        variant="h3"
        fontWeight={800}
        color={BRAND_SUCCESS}
        sx={{ letterSpacing: 8, mt: 0.5, fontFamily: 'monospace', textAlign: 'center' }}
      >
        {code || '------'}
      </Typography>

      <Box sx={{ mt: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: '#f1ddc9',
            '& .MuiLinearProgress-bar': { bgcolor: BRAND_SUCCESS },
          }}
        />
        <Typography variant="caption" color={BRAND_TEXT_SECONDARY} sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
          Обновится через {mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs} сек`}
        </Typography>
      </Box>

      <Typography variant="caption" color={BRAND_TEXT_SECONDARY} sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
        Введите телефон и код на постамате или назовите код сотруднику.
        <br />
        Не передавайте код посторонним.
      </Typography>
    </Paper>
  );
}
